/**
 * Bibliothèque : mes prises et mes projets studio.
 * `context` adapte l'action « Charger » :
 *  - editor : une prise remplace la prise en cours ; les projets s'ouvrent depuis le Studio
 *  - studio : une prise s'ajoute comme clip ; un projet remplace l'arrangement
 */
import { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import {
  listTakes, loadTake, deleteTake, type TakeRow,
  listProjects, loadProject, deleteProject, type ProjectRow, type ProjectSnapshot,
} from '../lib/library';
import { fmtTime } from '../format';
import { uuid } from '../lib/supabase';

interface Props {
  user: User;
  context: 'editor' | 'studio';
  onLoadTake: (buffer: AudioBuffer, row: TakeRow) => void;
  onLoadProject?: (snap: ProjectSnapshot, row: ProjectRow) => void;
  onClose: () => void;
}

export default function LibraryModal({ user, context, onLoadTake, onLoadProject, onClose }: Props) {
  const [tab, setTab] = useState<'takes' | 'projects'>('takes');
  const [takes, setTakes] = useState<TakeRow[] | null>(null);
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    listTakes().then(setTakes).catch((e) => setError(String(e.message ?? e)));
    listProjects().then(setProjects).catch((e) => setError(String(e.message ?? e)));
  };
  useEffect(refresh, []);

  const date = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" role="dialog" aria-label="Ma bibliothèque" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <strong>📚 Ma bibliothèque</strong>
          <button className="btn xs ghost" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        <div className="seg" role="tablist" aria-label="Type">
          <button role="tab" aria-selected={tab === 'takes'} className={tab === 'takes' ? 'on' : ''} onClick={() => setTab('takes')}>
            Prises {takes ? `(${takes.length})` : ''}
          </button>
          <button role="tab" aria-selected={tab === 'projects'} className={tab === 'projects' ? 'on alt' : ''} onClick={() => setTab('projects')}>
            Projets {projects ? `(${projects.length})` : ''}
          </button>
        </div>

        {error && <p className="account-error" role="alert">{error}</p>}

        {tab === 'takes' && (
          <div className="lib-list">
            {takes == null && <p className="note">Chargement…</p>}
            {takes?.length === 0 && <p className="note">Aucune prise sauvegardée. Le bouton ☁ enregistre la prise en cours dans ton compte.</p>}
            {takes?.map((r) => (
              <div key={r.id} className="lib-row">
                <div className="lib-meta">
                  <span className="export-name">{r.name}</span>
                  <span className="note">
                    {fmtTime(r.duration)} · {date(r.created_at)}
                    {r.bpm ? ` · ${Math.round(r.bpm)} BPM` : ''}{r.key_name ? ` · ${r.key_name}` : ''}
                  </span>
                </div>
                <button
                  className="btn xs" disabled={busy != null}
                  title={context === 'studio' ? 'Ajouter comme clip dans la piste sélectionnée' : 'Ouvrir cette prise dans l’éditeur'}
                  onClick={async () => {
                    setBusy(r.id);
                    try { onLoadTake(await loadTake(r), r); onClose(); }
                    catch (e) { setError(String((e as Error).message ?? e)); }
                    finally { setBusy(null); }
                  }}
                >{busy === r.id ? '…' : 'Charger'}</button>
                <button
                  className="btn xs ghost" disabled={busy != null} title="Supprimer de la bibliothèque"
                  onClick={async () => {
                    if (!window.confirm(`Supprimer « ${r.name} » de ta bibliothèque ?`)) return;
                    setBusy(r.id);
                    try { await deleteTake(r); refresh(); }
                    catch (e) { setError(String((e as Error).message ?? e)); }
                    finally { setBusy(null); }
                  }}
                >🗑</button>
              </div>
            ))}
          </div>
        )}

        {tab === 'projects' && (
          <div className="lib-list">
            {projects == null && <p className="note">Chargement…</p>}
            {projects?.length === 0 && <p className="note">Aucun projet sauvegardé. Dans le Studio, le bouton ☁ sauvegarde tout l’arrangement.</p>}
            {projects?.map((r) => (
              <div key={r.id} className="lib-row">
                <div className="lib-meta">
                  <span className="export-name">{r.name}</span>
                  <span className="note">
                    {r.data.tracks.length} piste{r.data.tracks.length > 1 ? 's' : ''} · {Math.round(r.bpm)} BPM · {date(r.updated_at)}
                  </span>
                </div>
                {context === 'studio' && onLoadProject ? (
                  <button
                    className="btn xs" disabled={busy != null} title="Remplacer l’arrangement actuel par ce projet"
                    onClick={async () => {
                      setBusy(r.id);
                      try { onLoadProject(await loadProject(user.id, r, uuid), r); onClose(); }
                      catch (e) { setError(String((e as Error).message ?? e)); }
                      finally { setBusy(null); }
                    }}
                  >{busy === r.id ? '…' : 'Charger'}</button>
                ) : (
                  <span className="note" title="Les projets multipistes s’ouvrent dans le Studio">via Studio ▸</span>
                )}
                <button
                  className="btn xs ghost" disabled={busy != null} title="Supprimer le projet et ses fichiers"
                  onClick={async () => {
                    if (!window.confirm(`Supprimer le projet « ${r.name} » ?`)) return;
                    setBusy(r.id);
                    try { await deleteProject(user.id, r); refresh(); }
                    catch (e) { setError(String((e as Error).message ?? e)); }
                    finally { setBusy(null); }
                  }}
                >🗑</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
